import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCashflows, useProfile, useResidencyCountries, useScenarios } from '../../hooks/useApi';
import { useExchangeRates } from '../../hooks/useExchangeRates';
import { formatCurrencyInput, extractNumericInput, formatAmount } from '../../utils/numberFormat';
import {
    CashFlowType,
    CashFlowCategory,
    DynamicCashFlow,
    Country,
    Currency,
    RetirementScenario,
    CASHFLOW_CATEGORY_LABELS,
    INCOME_CATEGORIES,
    EXPENSE_CATEGORIES,
    COUNTRY_CURRENCIES,
} from '../../types/api.types';
import styles from './CashflowPage.module.scss';

type ActiveTab = 'INCOME' | 'EXPENSE' | 'RETIREMENT';

const COUNTRIES: { value: Country; label: string }[] = [
    { value: 'US', label: 'United States' },
    { value: 'INDIA', label: 'India' },
    { value: 'CAN', label: 'Canada' },
];

const COUNTRY_DISPLAY: Record<Country, string> = { INDIA: 'India', US: 'USA', CAN: 'Canada' };

const DEFAULT_LIFE_EXPECTANCY: Record<Country, number> = { INDIA: 80, US: 83, CAN: 84 };

function getFireMultiplier(retirementYears: number): number {
    if (retirementYears <= 30) return 25;
    if (retirementYears <= 35) return 28;
    if (retirementYears <= 40) return 30;
    if (retirementYears <= 45) return 32;
    return 33;
}

function inflationAdjust(amount: number, growthRate: number, years: number): number {
    return amount * Math.pow(1 + growthRate / 100, years);
}

function convertCurrency(
    amount: number,
    fromCurrency: Currency,
    toCurrency: Currency,
    rates: Record<Currency, number>,
): number {
    if (fromCurrency === toCurrency) return amount;
    return (amount / rates[fromCurrency]) * rates[toCurrency];
}

const VALID_TABS: ActiveTab[] = ['INCOME', 'EXPENSE', 'RETIREMENT'];

export function CashflowPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get('tab')?.toUpperCase() as ActiveTab | null;
    const initialTab: ActiveTab = tabParam && VALID_TABS.includes(tabParam) ? tabParam : 'INCOME';
    const [activeTab, setActiveTab] = useState<ActiveTab>(initialTab);

    const handleTabChange = (tab: ActiveTab) => {
        setActiveTab(tab);
        setSearchParams({ tab }, { replace: true });
    };
    const [showForm, setShowForm] = useState(false);
    const [selectedCashflow, setSelectedCashflow] = useState<DynamicCashFlow | null>(null);
    const { cashflows, loading, error, refetch, addCashflow, updateCashflow, deleteCashflow } = useCashflows();
    const { scenarios, addScenario, setDefaultScenario } = useScenarios();
    const [activeScenarioId, setActiveScenarioId] = useState<string | null>(null);
    const [showScenarioForm, setShowScenarioForm] = useState(false);
    const [newScenarioName, setNewScenarioName] = useState('');
    const [newScenarioCountry, setNewScenarioCountry] = useState<Country>('INDIA');
    const [scenarioSaving, setScenarioSaving] = useState(false);
    const { profile } = useProfile();
    const { rates, loading: ratesLoading } = useExchangeRates();
    const baseCurrency = profile?.baseCurrency ?? 'USD';
    const currentAge = profile?.currentAge ?? 35;
    const profileRetirementAge = profile?.targetRetirementAge ?? 60;
    const yearsToRetirement = Math.max(0, profileRetirementAge - currentAge);

    const retirementCountry: Country = (() => {
        const history = profile?.residencyHistory ?? [];
        const atRetirement = history.find(
            (r) => profileRetirementAge >= r.startAge && profileRetirementAge <= r.endAge,
        );
        if (atRetirement) return atRetirement.country;
        const sorted = [...history].sort((a, b) => b.endAge - a.endAge);
        return sorted[0]?.country ?? 'US';
    })();
    const lifeExpectancy = DEFAULT_LIFE_EXPECTANCY[retirementCountry] ?? 83;
    const retirementYears = Math.max(0, lifeExpectancy - profileRetirementAge);
    const fireMultiplier = getFireMultiplier(retirementYears);

    let filtered: DynamicCashFlow[];
    let total: number;
    let oneTimeTotal = 0;
    let displayLabel: string;

    // Extra computations for Income/Expense summary banner
    let recurringMonthlyTotal = 0;
    let tabOneTimeTotal = 0;
    let recurringActiveCount = 0;
    let recurringTotalCount = 0;

    if (activeTab === 'RETIREMENT') {
        const allRetirement = cashflows.filter((cf) => cf.type === 'EXPENSE' && cf.isRetirementExpense);
        filtered = activeScenarioId
            ? allRetirement.filter((cf) => !cf.retirementScenarioId || cf.retirementScenarioId === activeScenarioId)
            : allRetirement;
        const recurringExpenses = filtered.filter((cf) => !cf.isOneTime);
        const oneTimeExpenses = filtered.filter((cf) => cf.isOneTime);
        total = recurringExpenses.reduce(
            (sum, cf) => sum + convertCurrency(cf.amount, cf.currency, baseCurrency, rates),
            0,
        );
        oneTimeTotal = oneTimeExpenses.reduce(
            (sum, cf) => sum + convertCurrency(cf.amount, cf.currency, baseCurrency, rates),
            0,
        );
        displayLabel = `MONTHLY RETIREMENT BUDGET (at age ${profileRetirementAge})`;
    } else {
        filtered = cashflows.filter(
            (cf) => cf.type === activeTab && (activeTab !== 'EXPENSE' || !cf.isRetirementExpense),
        );
        const activeAtCurrentAge = currentAge
            ? filtered.filter((cf) => currentAge >= cf.startAge && currentAge <= cf.endAge)
            : filtered;
        total = activeAtCurrentAge.reduce(
            (sum, cf) => sum + convertCurrency(cf.amount, cf.currency, baseCurrency, rates),
            0,
        );
        displayLabel = `MONTHLY ${activeTab} ${currentAge ? `AT AGE ${currentAge}` : '(ALL)'}`;

        const activeRecurring = activeAtCurrentAge.filter((cf) => !cf.isOneTime);
        const allOneTime = filtered.filter((cf) => cf.isOneTime);
        recurringMonthlyTotal = activeRecurring.reduce(
            (sum, cf) => sum + convertCurrency(cf.amount, cf.currency, baseCurrency, rates),
            0,
        );
        tabOneTimeTotal = allOneTime.reduce(
            (sum, cf) => sum + convertCurrency(cf.amount, cf.currency, baseCurrency, rates),
            0,
        );
        recurringActiveCount = activeRecurring.length;
        recurringTotalCount = filtered.filter((cf) => !cf.isOneTime).length;
    }

    if (loading || ratesLoading) {
        return (
            <div className={styles.page}>
                <p className={styles.loading}>Loading cashflows...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.page}>
                <p className={styles.error}>{error}</p>
                <button className={styles.retryButton} onClick={refetch}>
                    Retry
                </button>
            </div>
        );
    }

    const fireNumber = activeTab === 'RETIREMENT' ? total * 12 * fireMultiplier + oneTimeTotal : 0;

    const addLabel = activeTab === 'INCOME' ? 'Income Source' : activeTab === 'EXPENSE' ? 'Expense' : 'Retirement Item';
    const listLabel =
        activeTab === 'INCOME'
            ? 'Income Breakdown'
            : activeTab === 'EXPENSE'
              ? 'Expense Breakdown'
              : 'Retirement Expense Breakdown';

    return (
        <div className={styles.page}>
            {/* Page Header */}
            <div className={styles.pageHeader}>
                <div>
                    <h1 className={styles.title}>Income & Expenses</h1>
                    <p className={styles.subtitle}>Manage recurring and one-time cash flows.</p>
                </div>
                <div className={styles.headerActions}>
                    <div className={styles.tabs}>
                        <button
                            className={`${styles.tab} ${activeTab === 'INCOME' ? styles.active : ''}`}
                            onClick={() => handleTabChange('INCOME')}
                        >
                            Income
                        </button>
                        <button
                            className={`${styles.tab} ${activeTab === 'EXPENSE' ? styles.active : ''}`}
                            onClick={() => handleTabChange('EXPENSE')}
                        >
                            Expenses
                        </button>
                        <button
                            className={`${styles.tab} ${activeTab === 'RETIREMENT' ? styles.active : ''}`}
                            onClick={() => handleTabChange('RETIREMENT')}
                        >
                            Retirement
                        </button>
                    </div>
                    <button className={styles.addButton} onClick={() => setShowForm(true)}>
                        <span className="material-symbols-outlined">add_circle</span>
                        Add {addLabel}
                    </button>
                </div>
            </div>

            {/* ── RETIREMENT TAB ── */}
            {activeTab === 'RETIREMENT' && (
                <>
                    <div className={styles.scenarioNote}>
                        <strong>Retirement locations</strong> let you model the same life stage in different places —
                        e.g. India vs the US, or NYC vs a smaller city. Each location has its own expense set and can be
                        run through the simulator independently. The <em>Default</em> location drives your FIRE
                        dashboard number.
                    </div>

                    <div className={styles.scenarioRow}>
                        {scenarios.length > 0 && (
                            <div className={styles.scenarioTabs}>
                                <button
                                    className={`${styles.scenarioTab} ${activeScenarioId === null ? styles.activeScenario : ''}`}
                                    onClick={() => setActiveScenarioId(null)}
                                >
                                    All
                                </button>
                                {scenarios.map((s) => (
                                    <button
                                        key={s.scenarioId}
                                        className={`${styles.scenarioTab} ${activeScenarioId === s.scenarioId ? styles.activeScenario : ''}`}
                                        onClick={() => setActiveScenarioId(s.scenarioId)}
                                    >
                                        {s.name}
                                        {s.isDefault && <span className={styles.defaultBadge}>Default</span>}
                                    </button>
                                ))}
                            </div>
                        )}

                        {!showScenarioForm && (
                            <button className={styles.addScenarioButton} onClick={() => setShowScenarioForm(true)}>
                                + Add Location
                            </button>
                        )}

                        {showScenarioForm && (
                            <form
                                className={styles.scenarioForm}
                                onSubmit={async (e) => {
                                    e.preventDefault();
                                    if (!newScenarioName.trim()) return;
                                    setScenarioSaving(true);
                                    try {
                                        const created = await addScenario({
                                            name: newScenarioName.trim(),
                                            country: newScenarioCountry,
                                        });
                                        if (scenarios.length === 0) {
                                            await setDefaultScenario(created.scenarioId);
                                        }
                                        await refetch();
                                        setActiveScenarioId(created.scenarioId);
                                        setNewScenarioName('');
                                        setShowScenarioForm(false);
                                    } finally {
                                        setScenarioSaving(false);
                                    }
                                }}
                            >
                                <input
                                    autoFocus
                                    className={styles.scenarioInput}
                                    placeholder="e.g. India – Bangalore"
                                    value={newScenarioName}
                                    onChange={(e) => setNewScenarioName(e.target.value)}
                                    required
                                />
                                <select
                                    className={styles.scenarioSelect}
                                    value={newScenarioCountry}
                                    onChange={(e) => setNewScenarioCountry(e.target.value as Country)}
                                >
                                    <option value="INDIA">India</option>
                                    <option value="US">United States</option>
                                    <option value="CAN">Canada</option>
                                </select>
                                <button type="submit" className={styles.scenarioSaveButton} disabled={scenarioSaving}>
                                    {scenarioSaving ? 'Saving…' : 'Save'}
                                </button>
                                <button
                                    type="button"
                                    className={styles.scenarioCancelButton}
                                    onClick={() => setShowScenarioForm(false)}
                                >
                                    Cancel
                                </button>
                            </form>
                        )}
                    </div>

                    {/* Collapsible planning guide */}
                    <details className={styles.guide}>
                        <summary className={styles.guideSummary}>
                            <div className={styles.guideSummaryLeft}>
                                <span className="material-symbols-outlined">lightbulb</span>
                                <span className={styles.guideSummaryText}>
                                    Planning Guide: How to model retirement cashflows
                                </span>
                            </div>
                            <span className={`material-symbols-outlined ${styles.guideChevron}`}>expand_more</span>
                        </summary>
                        <div className={styles.guideBody}>
                            <ul className={styles.guideList}>
                                <li>
                                    <strong>Recurring:</strong> Monthly budget you expect every year in retirement
                                    (e.g., ₹2L/month living expenses). Grows with inflation.
                                </li>
                                <li>
                                    <strong>One-time:</strong> A single lump-sum spend (e.g., ₹50L home renovation).
                                    Deducted at whatever retirement age you test on the simulator slider.
                                </li>
                                <li>
                                    <strong>Already added in Goals?</strong> Don't add it here too — the simulator
                                    deducts both separately from your portfolio.
                                </li>
                            </ul>
                        </div>
                    </details>

                    {filtered.length > 0 && (
                        <div className={styles.retirementSummary}>
                            <div className={styles.summaryRow}>
                                <div className={styles.summaryItem}>
                                    <span className={styles.summaryLabel}>MONTHLY RECURRING</span>
                                    <span className={styles.summaryValue}>
                                        {formatAmount(Math.round(total), baseCurrency)}
                                    </span>
                                </div>
                                <div className={styles.summaryItem}>
                                    <span className={styles.summaryLabel}>ANNUAL RECURRING</span>
                                    <span className={styles.summaryValue}>
                                        {formatAmount(Math.round(total * 12), baseCurrency)}
                                    </span>
                                </div>
                                {oneTimeTotal > 0 && (
                                    <div className={styles.summaryItem}>
                                        <span className={styles.summaryLabel}>ONE-TIME EVENTS</span>
                                        <span className={styles.summaryValue}>
                                            {formatAmount(Math.round(oneTimeTotal), baseCurrency)}
                                        </span>
                                    </div>
                                )}
                            </div>
                            <div className={styles.summaryRow}>
                                <div className={styles.summaryItem}>
                                    <span className={styles.summaryLabel}>FIRE NUMBER ({fireMultiplier}×)</span>
                                    <span className={styles.summaryValue}>
                                        {formatAmount(Math.round(fireNumber), baseCurrency)}
                                    </span>
                                    <span className={styles.fireNumberNote}>
                                        {formatAmount(Math.round(total * 12), baseCurrency)}/yr × {fireMultiplier}
                                        {oneTimeTotal > 0
                                            ? ` + ${formatAmount(Math.round(oneTimeTotal), baseCurrency)} one-time`
                                            : ''}{' '}
                                        ({retirementYears}-yr horizon)
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className={styles.totalCard}>
                        <span className={styles.totalLabel}>{displayLabel}</span>
                        <span className={styles.totalValue}>{formatAmount(Math.round(total), baseCurrency)}</span>
                        <span className={styles.count}>
                            {filtered.filter((cf) => !cf.isOneTime).length} of {filtered.length} item(s)
                        </span>
                    </div>
                </>
            )}

            {/* ── INCOME / EXPENSE TABS ── */}
            {activeTab !== 'RETIREMENT' && (
                <>
                    {/* Summary Banner */}
                    <div className={styles.summaryBanner}>
                        <div className={styles.summaryGrid}>
                            <div className={styles.summaryCol}>
                                <span className={styles.summaryColLabel}>
                                    Monthly {activeTab === 'INCOME' ? 'Income' : 'Expenses'}
                                </span>
                                <span className={styles.summaryColValue}>
                                    {formatAmount(Math.round(recurringMonthlyTotal), baseCurrency)}
                                </span>
                                <span className={styles.summaryColNote}>Active at age {currentAge}</span>
                            </div>
                            <div className={styles.summaryCol}>
                                <span className={styles.summaryColLabel}>Annual Total</span>
                                <span className={styles.summaryColValue}>
                                    {formatAmount(Math.round(recurringMonthlyTotal * 12), baseCurrency)}
                                </span>
                                <span className={styles.summaryColNote}>Excl. growth adjustments</span>
                            </div>
                            <div className={styles.summaryCol}>
                                <span className={styles.summaryColLabel}>One-time Events</span>
                                <span className={styles.summaryColValue}>
                                    {formatAmount(Math.round(tabOneTimeTotal), baseCurrency)}
                                </span>
                                <span className={styles.summaryColNote}>
                                    Lump-sum {activeTab === 'INCOME' ? 'proceeds' : 'provisions'}
                                </span>
                            </div>
                            <div className={styles.summaryCol}>
                                <span className={styles.summaryColLabel}>
                                    Active {activeTab === 'INCOME' ? 'Sources' : 'Expenses'}
                                </span>
                                <span className={styles.summaryColValue}>{recurringActiveCount}</span>
                                <span className={styles.summaryColNote}>of {recurringTotalCount} total items</span>
                            </div>
                        </div>
                    </div>

                    {/* Collapsible planning guide */}
                    <details className={styles.guide}>
                        <summary className={styles.guideSummary}>
                            <div className={styles.guideSummaryLeft}>
                                <span className="material-symbols-outlined">lightbulb</span>
                                <span className={styles.guideSummaryText}>
                                    Planning Guide: How to model {activeTab === 'INCOME' ? 'income' : 'expense'}{' '}
                                    cashflows
                                </span>
                            </div>
                            <span className={`material-symbols-outlined ${styles.guideChevron}`}>expand_more</span>
                        </summary>
                        <div className={styles.guideBody}>
                            {activeTab === 'INCOME' ? (
                                <>
                                    <p>
                                        Income cashflows let you model different earning sources across your life —
                                        salary, freelance, rental income, or one-time proceeds.
                                    </p>
                                    <ul className={styles.guideList}>
                                        <li>
                                            <strong>Recurring:</strong> Monthly income active during a specific age
                                            range (e.g., salary from age 30 to retirement).
                                        </li>
                                        <li>
                                            <strong>One-time:</strong> A lump-sum receipt like a property sale or
                                            inheritance. Specify the age when it happens.
                                        </li>
                                        <li>
                                            <strong>Asset Sales:</strong> Treated as long-term capital gains. Enter the
                                            full proceeds; the simulator applies LTCG rates automatically.
                                        </li>
                                    </ul>
                                </>
                            ) : (
                                <>
                                    <p>
                                        Expense cashflows model your spending and major purchases during your working
                                        life.
                                    </p>
                                    <ul className={styles.guideList}>
                                        <li>
                                            <strong>Recurring:</strong> Monthly expenses active during a specific age
                                            range. Set a growth rate to account for inflation.
                                        </li>
                                        <li>
                                            <strong>One-time:</strong> Major purchases like a home, wedding, or
                                            education fee. Specify the age when it happens.
                                        </li>
                                        <li>
                                            <strong>Retirement expenses</strong> go in the Retirement tab — don't
                                            duplicate them here.
                                        </li>
                                    </ul>
                                </>
                            )}
                        </div>
                    </details>
                </>
            )}

            {/* ── SHARED: section header + list ── */}
            <div className={styles.listSectionHeader}>
                <h2 className={styles.listSectionTitle}>{listLabel}</h2>
            </div>

            {filtered.length === 0 ? (
                <button className={styles.addAnotherBtn} onClick={() => setShowForm(true)}>
                    <span className="material-symbols-outlined">add_circle</span>
                    <span>
                        Add your first{' '}
                        {activeTab === 'INCOME'
                            ? 'income source'
                            : activeTab === 'EXPENSE'
                              ? 'expense'
                              : 'retirement item'}
                    </span>
                </button>
            ) : (
                <>
                    <div className={styles.cashflowList}>
                        {filtered.map((cf) => (
                            <CashflowCard
                                key={cf.cashFlowId}
                                cashflow={cf}
                                onEdit={setSelectedCashflow}
                                onDelete={deleteCashflow}
                                yearsToEvent={activeTab === 'RETIREMENT' ? yearsToRetirement : 0}
                            />
                        ))}
                    </div>
                    <button className={styles.addAnotherBtn} onClick={() => setShowForm(true)}>
                        <span className="material-symbols-outlined">add_circle</span>
                        <span>
                            Add another{' '}
                            {activeTab === 'INCOME'
                                ? 'income source'
                                : activeTab === 'EXPENSE'
                                  ? 'expense'
                                  : 'retirement item'}
                        </span>
                    </button>
                </>
            )}

            {showForm && (
                <AddCashflowModal
                    type={activeTab === 'RETIREMENT' ? 'EXPENSE' : activeTab}
                    onClose={() => setShowForm(false)}
                    onAdded={() => {
                        setShowForm(false);
                        refetch();
                    }}
                    addCashflow={addCashflow}
                    targetRetirementAge={activeTab === 'RETIREMENT' ? profileRetirementAge : undefined}
                    scenarios={activeTab === 'RETIREMENT' ? scenarios : []}
                    preselectedScenarioId={activeTab === 'RETIREMENT' ? activeScenarioId : null}
                    preselectedCountry={
                        activeTab === 'RETIREMENT' && activeScenarioId
                            ? scenarios.find((s) => s.scenarioId === activeScenarioId)?.country
                            : undefined
                    }
                />
            )}

            {selectedCashflow && (
                <EditCashflowModal
                    cashflow={selectedCashflow}
                    onClose={() => setSelectedCashflow(null)}
                    onSaved={() => {
                        setSelectedCashflow(null);
                        refetch();
                    }}
                    updateCashflow={updateCashflow}
                    scenarios={scenarios}
                />
            )}
        </div>
    );
}

function CashflowCard({
    cashflow,
    onEdit,
    onDelete,
    yearsToEvent,
}: {
    cashflow: DynamicCashFlow;
    onEdit: (cashflow: DynamicCashFlow) => void;
    onDelete: (id: string) => void;
    yearsToEvent: number;
}) {
    const futureAmount =
        yearsToEvent > 0 ? inflationAdjust(cashflow.amount, cashflow.growthRate, yearsToEvent) : cashflow.amount;

    const countryLabel = COUNTRY_DISPLAY[cashflow.country] ?? cashflow.country;
    const frequency = cashflow.isOneTime ? 'Lump Sum' : 'Per Month';

    let timingText: string;
    if (cashflow.isOneTime) {
        timingText = cashflow.isRetirementExpense ? 'At retirement' : `Age ${cashflow.startAge}`;
    } else if (cashflow.isRetirementExpense) {
        timingText = `Retirement age–${cashflow.endAge}`;
    } else if (cashflow.endsAtRetirement) {
        timingText = `Age ${cashflow.startAge}–retirement`;
    } else {
        timingText = `Age ${cashflow.startAge}–${cashflow.endAge}`;
    }

    const cardAccentClass = cashflow.isOneTime
        ? styles.cardOneTime
        : cashflow.country === 'INDIA'
          ? styles.cardIndia
          : cashflow.country === 'US'
            ? styles.cardUS
            : styles.cardCAN;

    return (
        <div className={`${styles.card} ${cardAccentClass}`}>
            <div className={styles.cardInner}>
                <div className={styles.cardLeft}>
                    <div className={styles.cardLabelRow}>
                        <h3 className={styles.cardLabel}>
                            {cashflow.label || CASHFLOW_CATEGORY_LABELS[cashflow.category]}
                        </h3>
                        <span className={styles.cardCountryBadge}>{countryLabel}</span>
                        {cashflow.isOneTime && <span className={styles.oneTimeBadge}>One-time</span>}
                    </div>
                    <div className={styles.cardTimingRow}>
                        <div className={styles.cardTimingItem}>
                            <span className="material-symbols-outlined">
                                {cashflow.isOneTime ? 'event' : 'schedule'}
                            </span>
                            {timingText}
                        </div>
                        {!cashflow.isOneTime && cashflow.growthRate > 0 && (
                            <div className={styles.cardTimingItem}>
                                <span className="material-symbols-outlined">trending_up</span>
                                {cashflow.growthRate}%{' '}
                                {cashflow.type === 'INCOME' ? 'annual growth' : 'annual increase'}
                            </div>
                        )}
                    </div>
                    {yearsToEvent > 0 && futureAmount !== cashflow.amount && !cashflow.isOneTime && (
                        <div className={styles.inflationNote}>
                            {formatAmount(cashflow.amount, cashflow.currency)} today →{' '}
                            {formatAmount(Math.round(futureAmount), cashflow.currency)} at retirement
                        </div>
                    )}
                </div>
                <div className={styles.cardRight}>
                    <div className={styles.cardAmountBlock}>
                        <p className={styles.cardAmount}>{formatAmount(cashflow.amount, cashflow.currency)}</p>
                        <p className={styles.cardFrequency}>{frequency}</p>
                    </div>
                    <div className={styles.cardIconActions}>
                        <button className={styles.iconBtnEdit} onClick={() => onEdit(cashflow)} title="Edit">
                            <span className="material-symbols-outlined">edit</span>
                        </button>
                        <button
                            className={styles.iconBtnDelete}
                            onClick={() => onDelete(cashflow.cashFlowId)}
                            title="Delete"
                        >
                            <span className="material-symbols-outlined">delete</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

interface EditCashflowModalProps {
    cashflow: DynamicCashFlow;
    onClose: () => void;
    onSaved: () => void;
    updateCashflow: (
        cashFlowId: string,
        patch: Partial<Omit<DynamicCashFlow, 'cashFlowId' | 'memberSub' | 'updatedAt'>>,
    ) => Promise<DynamicCashFlow>;
    scenarios: RetirementScenario[];
}

function EditCashflowModal({ cashflow, onClose, onSaved, updateCashflow, scenarios }: EditCashflowModalProps) {
    const { rates } = useExchangeRates();
    const { profile } = useProfile();
    const baseCurrency = profile?.baseCurrency ?? 'USD';
    const categories = cashflow.type === 'INCOME' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;
    const [category, setCategory] = useState<CashFlowCategory>(cashflow.category);
    const [label, setLabel] = useState(cashflow.label ?? '');
    const [country, setCountry] = useState<Country>(cashflow.country);
    const currency = COUNTRY_CURRENCIES[country];
    const [amount, setAmount] = useState(String(cashflow.amount));
    const [growthRate, setGrowthRate] = useState(String(cashflow.growthRate));
    const [startAge, setStartAge] = useState(String(cashflow.startAge));
    const [endAge, setEndAge] = useState(String(cashflow.endAge));
    const [isOneTime] = useState(!!cashflow.isOneTime);
    const [endsAtRetirement, setEndsAtRetirement] = useState(!!cashflow.endsAtRetirement);
    const [retirementScenarioId, setRetirementScenarioId] = useState<string>(cashflow.retirementScenarioId ?? '');
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);

    const numericAmount = Number(extractNumericInput(amount));
    const convertedAmount =
        numericAmount > 0 && currency !== baseCurrency
            ? convertCurrency(numericAmount, currency, baseCurrency, rates)
            : numericAmount;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setFormError(null);
        try {
            await updateCashflow(cashflow.cashFlowId, {
                category,
                label: label || undefined,
                country,
                amount: Number(extractNumericInput(amount)),
                currency,
                growthRate: Number(growthRate),
                startAge: cashflow.isRetirementExpense && !isOneTime ? 0 : Number(startAge),
                endAge: isOneTime ? Number(startAge) : Number(endAge),
                isOneTime: isOneTime || undefined,
                isRetirementExpense: cashflow.isRetirementExpense || undefined,
                endsAtRetirement: endsAtRetirement || undefined,
                ...(cashflow.isRetirementExpense && { retirementScenarioId: retirementScenarioId || null }),
            });
            onSaved();
        } catch (err) {
            setFormError(err instanceof Error ? err.message : 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h2>Edit {cashflow.type === 'INCOME' ? 'Income' : 'Expense'}</h2>
                    <button className={styles.closeButton} onClick={onClose}>
                        &times;
                    </button>
                </div>
                <form className={styles.form} onSubmit={handleSubmit}>
                    <div className={styles.field}>
                        <label className={styles.label}>Label (optional)</label>
                        <input
                            type="text"
                            className={styles.input}
                            placeholder={
                                cashflow.type === 'INCOME'
                                    ? 'e.g., Tech job, Freelance consulting'
                                    : "e.g., Son's college, Annual vacation"
                            }
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                        />
                    </div>

                    <div className={styles.field}>
                        <label className={styles.label}>Category</label>
                        <select
                            className={styles.input}
                            value={category}
                            onChange={(e) => setCategory(e.target.value as CashFlowCategory)}
                        >
                            {categories.map((c) => (
                                <option key={c} value={c}>
                                    {CASHFLOW_CATEGORY_LABELS[c]}
                                </option>
                            ))}
                        </select>
                        {category === 'ASSET_SALE' && (
                            <p className={styles.ltcgNote}>
                                Treated as long-term capital gains — India 12.5%, US 0–20% based on your income that
                                year, Canada 50% inclusion at marginal rate. Enter the full proceeds; the simulation
                                applies LTCG rates automatically.
                            </p>
                        )}
                    </div>

                    <div className={styles.field}>
                        <label className={styles.label}>Country</label>
                        <select
                            className={styles.input}
                            value={country}
                            onChange={(e) => setCountry(e.target.value as Country)}
                        >
                            {COUNTRIES.map((c) => (
                                <option key={c.value} value={c.value}>
                                    {c.label}
                                </option>
                            ))}
                        </select>
                        <div className={styles.currencyIndicator}>
                            Enter amount in <strong>{currency}</strong> (local currency)
                        </div>
                    </div>

                    {isOneTime ? (
                        <div className={styles.field}>
                            <label className={styles.label}>Total Amount</label>
                            <input
                                type="text"
                                inputMode="numeric"
                                className={styles.input}
                                value={formatCurrencyInput(amount, currency)}
                                onChange={(e) => setAmount(extractNumericInput(e.target.value))}
                                required
                            />
                            {numericAmount > 0 && currency !== baseCurrency && (
                                <div className={styles.conversionNote}>
                                    ≈ {formatAmount(Math.round(convertedAmount), baseCurrency)} {baseCurrency}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className={styles.row}>
                            <div className={styles.field}>
                                <label className={styles.label}>Monthly Amount</label>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    className={styles.input}
                                    value={formatCurrencyInput(amount, currency)}
                                    onChange={(e) => setAmount(extractNumericInput(e.target.value))}
                                    required
                                />
                                {numericAmount > 0 && currency !== baseCurrency && (
                                    <div className={styles.conversionNote}>
                                        ≈ {formatAmount(Math.round(convertedAmount), baseCurrency)} {baseCurrency}
                                    </div>
                                )}
                            </div>
                            <div className={styles.field}>
                                <label className={styles.label}>
                                    {cashflow.type === 'INCOME' ? 'Annual Growth %' : 'Annual Increase %'}
                                </label>
                                <input
                                    type="number"
                                    className={styles.input}
                                    value={growthRate}
                                    onChange={(e) => setGrowthRate(e.target.value)}
                                    step={0.5}
                                    min={0}
                                    max={20}
                                />
                                <span className={styles.fieldHint}>
                                    {cashflow.type === 'INCOME'
                                        ? 'Expected yearly raise or investment growth rate'
                                        : 'Expected yearly cost increase — approx. your local inflation rate'}
                                </span>
                            </div>
                        </div>
                    )}

                    {!cashflow.isRetirementExpense && !isOneTime && (
                        <div className={styles.field}>
                            <label className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={endsAtRetirement}
                                    onChange={(e) => setEndsAtRetirement(e.target.checked)}
                                />
                                Ends when I retire
                            </label>
                        </div>
                    )}

                    {isOneTime && cashflow.isRetirementExpense ? (
                        <div className={styles.atRetirementNote}>
                            No fixed age needed — this will be deducted at whatever retirement age you test on the
                            simulator slider.
                        </div>
                    ) : cashflow.isRetirementExpense && !isOneTime ? (
                        <div className={styles.row}>
                            <div className={styles.field}>
                                <p className={styles.retirementStartNote}>Starts at retirement age</p>
                            </div>
                            <div className={styles.field}>
                                <label className={styles.label}>End Age</label>
                                <input
                                    type="number"
                                    className={styles.input}
                                    value={endAge}
                                    onChange={(e) => setEndAge(e.target.value)}
                                    min={18}
                                    max={100}
                                    required
                                />
                            </div>
                        </div>
                    ) : (
                        <div className={styles.row}>
                            <div className={styles.field}>
                                <label className={styles.label}>{isOneTime ? 'Event Age' : 'Start Age'}</label>
                                <input
                                    type="number"
                                    className={styles.input}
                                    value={startAge}
                                    onChange={(e) => setStartAge(e.target.value)}
                                    min={18}
                                    max={90}
                                    required
                                />
                            </div>
                            {!isOneTime && !endsAtRetirement && (
                                <div className={styles.field}>
                                    <label className={styles.label}>End Age</label>
                                    <input
                                        type="number"
                                        className={styles.input}
                                        value={endAge}
                                        onChange={(e) => setEndAge(e.target.value)}
                                        min={Number(startAge)}
                                        max={100}
                                        required
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {cashflow.isRetirementExpense && scenarios.length > 0 && (
                        <div className={styles.field}>
                            <label className={styles.label}>Retirement Scenario (optional)</label>
                            <select
                                className={styles.input}
                                value={retirementScenarioId}
                                onChange={(e) => setRetirementScenarioId(e.target.value)}
                            >
                                <option value="">General (applies to all scenarios)</option>
                                {scenarios.map((s) => (
                                    <option key={s.scenarioId} value={s.scenarioId}>
                                        {s.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {formError && <p className={styles.formError}>{formError}</p>}

                    <div className={styles.actions}>
                        <button type="button" className={styles.cancelButton} onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className={styles.submitButton} disabled={saving}>
                            {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

interface AddCashflowModalProps {
    type: CashFlowType;
    onClose: () => void;
    onAdded: () => void;
    addCashflow: (cf: Omit<DynamicCashFlow, 'cashFlowId' | 'memberSub' | 'updatedAt'>) => Promise<DynamicCashFlow>;
    targetRetirementAge?: number;
    scenarios?: RetirementScenario[];
    preselectedScenarioId?: string | null;
    preselectedCountry?: Country;
}

function AddCashflowModal({
    type,
    onClose,
    onAdded,
    addCashflow,
    targetRetirementAge,
    scenarios = [],
    preselectedScenarioId,
    preselectedCountry,
}: AddCashflowModalProps) {
    const { rates } = useExchangeRates();
    const { profile } = useProfile();
    const { countryOptions, countries, retirementCountry } = useResidencyCountries();
    const baseCurrency = profile?.baseCurrency ?? 'USD';
    const categories = type === 'INCOME' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;
    const [category, setCategory] = useState<CashFlowCategory>(categories[0]);
    const [label, setLabel] = useState('');
    const [country, setCountry] = useState<Country>(preselectedCountry ?? countries[0] ?? 'US');

    useEffect(() => {
        if (preselectedCountry) {
            setCountry(preselectedCountry);
        } else if (targetRetirementAge && retirementCountry) {
            setCountry(retirementCountry);
        } else if (!targetRetirementAge && countries[0]) {
            setCountry(countries[0]);
        }
    }, [preselectedCountry, retirementCountry, countries, targetRetirementAge]);
    const currency = COUNTRY_CURRENCIES[country];
    const [amount, setAmount] = useState('');
    const [growthRate, setGrowthRate] = useState('3');
    const [startAge, setStartAge] = useState(targetRetirementAge?.toString() ?? '35');
    const [endAge, setEndAge] = useState(targetRetirementAge ? (targetRetirementAge + 30).toString() : '65');
    const [isOneTime, setIsOneTime] = useState(false);
    const [endsAtRetirement, setEndsAtRetirement] = useState(type === 'EXPENSE' && !targetRetirementAge);
    const [retirementScenarioId, setRetirementScenarioId] = useState<string>(preselectedScenarioId ?? '');
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);

    const numericAmount = Number(extractNumericInput(amount));
    const convertedAmount =
        numericAmount > 0 && currency !== baseCurrency
            ? convertCurrency(numericAmount, currency, baseCurrency, rates)
            : numericAmount;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setFormError(null);
        try {
            await addCashflow({
                type,
                category,
                label: label || undefined,
                country,
                amount: Number(amount),
                currency,
                growthRate: Number(growthRate),
                startAge: targetRetirementAge !== undefined && !isOneTime ? 0 : Number(startAge),
                endAge: isOneTime ? Number(startAge) : endsAtRetirement ? 95 : Number(endAge),
                isOneTime: isOneTime || undefined,
                isRetirementExpense: targetRetirementAge !== undefined || undefined,
                endsAtRetirement: endsAtRetirement || undefined,
                ...(targetRetirementAge !== undefined &&
                    retirementScenarioId && {
                        retirementScenarioId,
                    }),
            });
            onAdded();
        } catch (err) {
            setFormError(err instanceof Error ? err.message : 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h2>
                        Add{' '}
                        {type === 'INCOME'
                            ? 'Income'
                            : targetRetirementAge !== undefined
                              ? 'Retirement Expense'
                              : 'Expense'}
                    </h2>
                    <button className={styles.closeButton} onClick={onClose}>
                        &times;
                    </button>
                </div>
                <form className={styles.form} onSubmit={handleSubmit}>
                    <div className={styles.field}>
                        <label className={styles.label}>Label (optional)</label>
                        <input
                            type="text"
                            className={styles.input}
                            placeholder={
                                type === 'INCOME'
                                    ? 'e.g., Tech job, Freelance consulting'
                                    : "e.g., Son's college, Annual vacation"
                            }
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                        />
                    </div>

                    <div className={styles.field}>
                        <label className={styles.label}>Category</label>
                        <select
                            className={styles.input}
                            value={category}
                            onChange={(e) => setCategory(e.target.value as CashFlowCategory)}
                        >
                            {categories.map((c) => (
                                <option key={c} value={c}>
                                    {CASHFLOW_CATEGORY_LABELS[c]}
                                </option>
                            ))}
                        </select>
                        {category === 'ASSET_SALE' && (
                            <p className={styles.ltcgNote}>
                                Treated as long-term capital gains — India 12.5%, US 0–20% based on your income that
                                year, Canada 50% inclusion at marginal rate. Enter the full proceeds; the simulation
                                applies LTCG rates automatically.
                            </p>
                        )}
                    </div>

                    <div className={styles.field}>
                        <label className={styles.label}>Country</label>
                        <select
                            className={styles.input}
                            value={country}
                            onChange={(e) => setCountry(e.target.value as Country)}
                        >
                            {countryOptions.map((c) => (
                                <option key={c.value} value={c.value}>
                                    {c.label}
                                </option>
                            ))}
                        </select>
                        <div className={styles.currencyIndicator}>
                            Enter amount in <strong>{currency}</strong> (local currency)
                        </div>
                    </div>

                    {(type === 'EXPENSE' || type === 'INCOME') && (
                        <div className={styles.field}>
                            <label className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={isOneTime}
                                    onChange={(e) => setIsOneTime(e.target.checked)}
                                />
                                {type === 'INCOME'
                                    ? 'One-time proceeds (e.g., property sale, inheritance)'
                                    : 'One-time event (e.g., home purchase, wedding)'}
                            </label>
                            {isOneTime && targetRetirementAge !== undefined && type === 'EXPENSE' && (
                                <p className={styles.warnNote}>
                                    If you've also added this as a Goal, remove one — both are deducted from your
                                    portfolio independently in the simulation.
                                </p>
                            )}
                        </div>
                    )}

                    {!targetRetirementAge && !isOneTime && (
                        <div className={styles.field}>
                            <label className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={endsAtRetirement}
                                    onChange={(e) => setEndsAtRetirement(e.target.checked)}
                                />
                                Ends when I retire
                            </label>
                        </div>
                    )}

                    {isOneTime ? (
                        <div className={styles.field}>
                            <label className={styles.label}>Total Amount</label>
                            <input
                                type="text"
                                inputMode="numeric"
                                className={styles.input}
                                value={formatCurrencyInput(amount, currency)}
                                onChange={(e) => setAmount(extractNumericInput(e.target.value))}
                                required
                            />
                            {numericAmount > 0 && currency !== baseCurrency && (
                                <div className={styles.conversionNote}>
                                    ≈ {formatAmount(Math.round(convertedAmount), baseCurrency)} {baseCurrency}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className={styles.row}>
                            <div className={styles.field}>
                                <label className={styles.label}>Monthly Amount</label>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    className={styles.input}
                                    value={formatCurrencyInput(amount, currency)}
                                    onChange={(e) => setAmount(extractNumericInput(e.target.value))}
                                    required
                                />
                                {numericAmount > 0 && currency !== baseCurrency && (
                                    <div className={styles.conversionNote}>
                                        ≈ {formatAmount(Math.round(convertedAmount), baseCurrency)} {baseCurrency}
                                    </div>
                                )}
                            </div>
                            <div className={styles.field}>
                                <label className={styles.label}>
                                    {type === 'INCOME' ? 'Annual Growth %' : 'Annual Increase %'}
                                </label>
                                <input
                                    type="number"
                                    className={styles.input}
                                    value={growthRate}
                                    onChange={(e) => setGrowthRate(e.target.value)}
                                    step={0.5}
                                    min={0}
                                    max={20}
                                />
                                <span className={styles.fieldHint}>
                                    {type === 'INCOME'
                                        ? 'Expected yearly raise or investment growth rate'
                                        : 'Expected yearly cost increase — approx. your local inflation rate'}
                                </span>
                            </div>
                        </div>
                    )}

                    {isOneTime && targetRetirementAge !== undefined ? (
                        <div className={styles.atRetirementNote}>
                            No fixed age needed — this will be deducted at whatever retirement age you test on the
                            simulator slider. Drag the slider to compare how it affects retiring at 55 vs 60 vs 65.
                        </div>
                    ) : targetRetirementAge !== undefined && !isOneTime ? (
                        <div className={styles.row}>
                            <div className={styles.field}>
                                <p className={styles.retirementStartNote}>Starts at retirement age</p>
                            </div>
                            <div className={styles.field}>
                                <label className={styles.label}>End Age</label>
                                <input
                                    type="number"
                                    className={styles.input}
                                    value={endAge}
                                    onChange={(e) => setEndAge(e.target.value)}
                                    min={18}
                                    max={100}
                                    required
                                />
                            </div>
                        </div>
                    ) : (
                        <div className={styles.row}>
                            <div className={styles.field}>
                                <label className={styles.label}>{isOneTime ? 'Event Age' : 'Start Age'}</label>
                                <input
                                    type="number"
                                    className={styles.input}
                                    value={startAge}
                                    onChange={(e) => setStartAge(e.target.value)}
                                    min={18}
                                    max={90}
                                    required
                                />
                            </div>
                            {!isOneTime && !endsAtRetirement && (
                                <div className={styles.field}>
                                    <label className={styles.label}>End Age</label>
                                    <input
                                        type="number"
                                        className={styles.input}
                                        value={endAge}
                                        onChange={(e) => setEndAge(e.target.value)}
                                        min={Number(startAge)}
                                        max={100}
                                        required
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {targetRetirementAge !== undefined && scenarios.length > 0 && (
                        <div className={styles.field}>
                            <label className={styles.label}>Retirement Scenario (optional)</label>
                            <select
                                className={styles.input}
                                value={retirementScenarioId}
                                onChange={(e) => setRetirementScenarioId(e.target.value)}
                            >
                                <option value="">General (applies to all scenarios)</option>
                                {scenarios.map((s) => (
                                    <option key={s.scenarioId} value={s.scenarioId}>
                                        {s.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {formError && <p className={styles.formError}>{formError}</p>}

                    <div className={styles.actions}>
                        <button type="button" className={styles.cancelButton} onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className={styles.submitButton} disabled={saving}>
                            {saving ? 'Saving...' : 'Add'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
